// @flow

import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import gql from 'graphql-tag';
import { compose, setDisplayName, withProps } from 'recompose';
import RPath from 'ramda/src/path';
import { format } from 'url';
import WebformForbidden from './WebformForbidden';
import logError from '../utils/logError';

import {
  injectSheet,
  Main,
  type InjectSheetProvidedProps
} from '@catalytic/catalytic-ui';
import { useLocation } from 'react-router-dom';
import { LOGOUT } from '../const/path';

import Webform from './Webform';
import WebformNotFound from './WebformNotFound';
import TopBarLoader from '../Loading/TopBarLoader';
import { FieldFragment } from '../Field/FieldTypeDefs';
import isUUID from '../utils/uuid';

const IconFragment = gql`
  fragment IconFragment on File {
    id
    appleTouchIcon: url(input: { width: 180, format: PNG })
    icon16: url(input: { width: 16, format: PNG })
    icon32: url(input: { width: 32, format: PNG })
    shortCutIcon: url(input: { width: 48, format: PNG })
  }
`;
const WebformFragment = gql`
  ${FieldFragment}

  fragment WebformFragment on Webhook {
    description
    displayName
    formSubmitButtonText
    formDescription
    formFavicon
    formLogo
    formTitle
    id
    inputs {
      id
      ...FieldFragment
    }
    name
    processID
  }
`;
export const TEAM_QUERY = gql`
  ${IconFragment}

  query Team {
    team {
      id
      displayName
      icon {
        id
        ...IconFragment
      }
      thumbnail {
        id
        url
      }
    }
  }
`;
export const WEBFORM_QUERY = gql`
  ${WebformFragment}

  query Webform($name: String!) {
    webform(name: $name) {
      id
      ...WebformFragment
    }
  }
`;
export const WEBHOOK_QUERY = gql`
  ${WebformFragment}

  query Webhook($id: ID!) {
    webhook(id: $id) {
      id
      ...WebformFragment
    }
  }
`;
const ICON_QUERY = gql`
  ${IconFragment}

  query Icon($id: ID!) {
    file(id: $id) {
      id
      ...IconFragment
    }
  }
`;
const EXECUTE_WEBHOOK = gql`
  mutation ExecuteWebhook($id: String!, $inputs: [FieldInput]) {
    executeWebhook(input: { id: $id, inputs: $inputs }) {
      contextReference {
        reference {
          id
        }
      }
    }
  }
`;

export const Style = () => ({
  root: {
    paddingTop: '1.5rem',
    paddingBottom: '1.313rem'
  }
});

type Props = InjectSheetProvidedProps & {
  id?: string,
  isWebhook: boolean,
  name?: string,
  query: typeof WEBFORM_QUERY | typeof WEBHOOK_QUERY,
  variables: { string: any }
};

export const WebformContainer = (props: Props) => {
  const [errorType, setErrorType] = useState([]);

  const location = useLocation();
  const { classes, id, isWebhook, name, query, variables } = props;
  const search = RPath(['location', 'search'], props);
  const params = (new URLSearchParams(search): URLSearchParams);
  const hideHeader = !!(
    params &&
    params.get('hide-header') &&
    params.get('hide-header') === 'true'
  );
  const hideFooter = !!(
    params &&
    params.get('hide-footer') &&
    params.get('hide-footer') === 'true'
  );
  const { loading: teamLoading, ...teamQueryResult } = useQuery(TEAM_QUERY, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    onError: error => logError(error)
  });

  const { loading: loadingWebformQuery, ...webformQueryResult } = useQuery(
    query,
    {
      variables: variables,
      fetchPolicy: 'network-only',
      onError: error => {
        const errorMessages = error.graphQLErrors.map(errors => errors.message);
        if (errorMessages.includes('Unauthorized')) {
          browserRedirect(authUrl);
        }
        setErrorType(errorMessages);
        logError(error);
      }
    }
  );
  const form = RPath(
    [isWebhook ? 'webhook' : 'webform'],
    webformQueryResult?.data
  );
  const { formFavicon } = form || {};
  const { loading: loadingIconQuery, ...iconQueryResult } = useQuery(
    ICON_QUERY,
    {
      variables: { id: formFavicon },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      skip: !(
        formFavicon &&
        typeof formFavicon === 'string' &&
        isUUID(formFavicon)
      ),
      onError: error => logError(error)
    }
  );
  const [executeWebhook, mutationResult] = useMutation(EXECUTE_WEBHOOK, {
    onError: error => logError(error)
  });

  const referrer = (location.pathname || '') + (location.search || '');
  const authUrl = format({
    pathname: LOGOUT,
    query: {
      referrer
    }
  });
  const browserRedirect = (authPath: string) => {
    window.location = authPath;
    return null;
  };
  if (errorType.includes('Forbidden')) {
    return (
      <Main className={classes.root}>
        <WebformForbidden />
      </Main>
    );
  }
  if (errorType.length > 0 && !errorType.includes('Unauthorized')) {
    return (
      <Main className={classes.root}>
        <WebformNotFound params={params} />
      </Main>
    );
  }
  if (!query || !variables) {
    return (
      <Main className={classes.root}>
        <WebformNotFound params={params} />
      </Main>
    );
  }
  if (
    !teamQueryResult?.data ||
    teamLoading ||
    loadingIconQuery ||
    !webformQueryResult?.data ||
    loadingWebformQuery
  ) {
    return <TopBarLoader />;
  }
  return (
    <Main className={classes.root}>
      <Webform
        executeWebhook={executeWebhook}
        hideHeader={hideHeader}
        hideFooter={hideFooter}
        iconQueryResult={iconQueryResult}
        id={id}
        isWebhook={isWebhook}
        mutationResult={mutationResult}
        name={name}
        params={params}
        teamQueryResult={teamQueryResult}
        webformQueryResult={webformQueryResult}
      />
    </Main>
  );
};

WebformContainer.displayName = 'WebformContainer';

export default compose(
  injectSheet(Style),
  setDisplayName('WebformContainer'),
  withProps(props => {
    const id = RPath(['match', 'params', 'id'], props);
    const name = RPath(['match', 'params', 'name'], props);

    return {
      // Handle /form/:name and /webform/:id routes
      ...(id && {
        id,
        isWebhook: true,
        query: WEBHOOK_QUERY,
        variables: { id }
      }),
      ...(name && {
        isWebhook: false,
        name,
        query: WEBFORM_QUERY,
        variables: { name }
      })
    };
  })
)(WebformContainer);
